import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../../context/AuthContext';
import * as userService from '../../services/userService';

// Material UI components
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Chip,
  Avatar,
  Alert,
  AlertTitle,
  Tooltip,
  CircularProgress,
  Snackbar,
  Divider
} from '@mui/material';

// Material UI icons
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  ShoppingCart as ShoppingCartIcon,
  Warehouse as WarehouseIcon,
  Cancel as CancelIcon,
  Save as SaveIcon
} from '@mui/icons-material';

const UserManagement = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'warehouse_staff',
    is_active: true
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const data = await userService.getUsers();
        setUsers(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleOpenDialog = (type, user = null) => {
    setDialogType(type);
    setSelectedUser(user);

    if (type === 'add') {
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'warehouse_staff',
        is_active: true
      });
    } else if (type === 'edit' && user) {
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        role: user.role,
        is_active: user.active
      });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleStatusChange = (e) => {
    setFormData({
      ...formData,
      is_active: e.target.value === 'active'
    });
  };

  const handleCreateUser = async () => {
    try {
      const userData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role
      };

      const newUser = await userService.createUser(userData);

      setUsers([...users, newUser]);
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: 'User created successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error creating user:', err);
      setSnackbar({
        open: true,
        message: `Failed to create user: ${err.response?.data?.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const updateData = {
        email: formData.email,
        role: formData.role,
        is_active: formData.is_active
      };

      // Only include password if it was provided
      if (formData.password) {
        updateData.password = formData.password;
      }

      const updatedUser = await userService.updateUser(selectedUser.id, updateData);

      setUsers(users.map(user =>
        user.id === selectedUser.id ? updatedUser : user
      ));

      handleCloseDialog();
      setSnackbar({
        open: true,
        message: 'User updated successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating user:', err);
      setSnackbar({
        open: true,
        message: `Failed to update user: ${err.response?.data?.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await userService.deleteUser(selectedUser.id);

      setUsers(users.filter(user => user.id !== selectedUser.id));
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting user:', err);
      setSnackbar({
        open: true,
        message: `Failed to delete user: ${err.response?.data?.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Helper function to get role chip color and icon
  const getRoleChip = (role) => {
    let color, icon, label;

    switch (role) {
      case 'manager':
        color = 'primary';
        icon = <AdminIcon />;
        label = 'Manager';
        break;
      case 'purchaser':
        color = 'success';
        icon = <ShoppingCartIcon />;
        label = 'Purchaser';
        break;
      case 'warehouse_staff':
        color = 'info';
        icon = <WarehouseIcon />;
        label = 'Warehouse Staff';
        break;
      default:
        color = 'default';
        icon = <PersonIcon />;
        label = role;
    }

    return (
      <Chip
        icon={icon}
        label={label}
        color={color}
        size="small"
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Check if current user is trying to edit themselves
  const isSelfEdit = (userId) => {
    return currentUser && currentUser.id === userId;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          User Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={() => handleOpenDialog('add')}
        >
          Add New User
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 4 }}
        >
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {/* User Table */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading users...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1">
                      No users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    sx={{
                      ...(user.active === false && {
                        bgcolor: 'action.hover',
                        '& td': { color: 'text.disabled' }
                      })
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1,
                            bgcolor: user.active ? 'primary.main' : 'grey.500'
                          }}
                        >
                          {user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {user.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{getRoleChip(user.role)}</TableCell>
                    <TableCell>{formatDate(user.updated_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.active ? "Active" : "Inactive"}
                        color={user.active ? "success" : "default"}
                        size="small"
                        variant={user.active ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog('edit', user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isSelfEdit(user.id) ? "Cannot delete your own account" : "Delete User"}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDialog('delete', user)}
                              disabled={isSelfEdit(user.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialogs */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        {dialogType === 'add' && (
          <>
            <DialogTitle>Add New User</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Account Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      autoFocus
                      required
                      fullWidth
                      id="username"
                      name="username"
                      label="Username"
                      type="text"
                      value={formData.username}
                      onChange={handleInputChange}
                      helperText="Choose a unique username for the account"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      id="email"
                      name="email"
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      helperText="Work email address"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      id="password"
                      name="password"
                      label="Password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      helperText="Must be at least 8 characters"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Role & Permissions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="role-label">Role</InputLabel>
                      <Select
                        labelId="role-label"
                        id="role"
                        name="role"
                        label="Role"
                        value={formData.role}
                        onChange={handleInputChange}
                      >
                        <MenuItem value="manager">Manager</MenuItem>
                        <MenuItem value="purchaser">Purchaser</MenuItem>
                        <MenuItem value="warehouse_staff">Warehouse Staff</MenuItem>
                      </Select>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, ml: 2 }}>
                        Determines user's permissions in the system
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleCreateUser}
                disabled={!formData.username || !formData.email || !formData.password}
              >
                Add User
              </Button>
            </DialogActions>
          </>
        )}

        {dialogType === 'edit' && selectedUser && (
          <>
            <DialogTitle>Edit User</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Account Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      autoFocus
                      fullWidth
                      id="username"
                      label="Username"
                      type="text"
                      value={formData.username}
                      disabled
                      helperText="Username cannot be changed"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="email"
                      name="email"
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      helperText="Work email address"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="password"
                      name="password"
                      label="New Password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      helperText="Leave blank to keep current password"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Role & Permissions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="role-label">Role</InputLabel>
                      <Select
                        labelId="role-label"
                        id="role"
                        name="role"
                        label="Role"
                        value={formData.role}
                        onChange={handleInputChange}
                        disabled={isSelfEdit(selectedUser.id)}
                        sx={{ minWidth: '200px' }}
                      >
                        <MenuItem value="manager">Manager</MenuItem>
                        <MenuItem value="purchaser">Purchaser</MenuItem>
                        <MenuItem value="warehouse_staff">Warehouse Staff</MenuItem>
                      </Select>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, ml: 2 }}>
                        Determines user's permissions in the system
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="status-label">Status</InputLabel>
                      <Select
                        labelId="status-label"
                        id="status"
                        label="Status"
                        value={formData.is_active ? 'active' : 'inactive'}
                        onChange={handleStatusChange}
                        disabled={isSelfEdit(selectedUser.id)}
                        sx={{ minWidth: '200px' }}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, ml: 2 }}>
                        Control user's access to the system
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleUpdateUser}
              >
                Save Changes
              </Button>
            </DialogActions>
          </>
        )}

        {dialogType === 'delete' && selectedUser && (
          <>
            <DialogTitle>Delete User</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete the user "{selectedUser.username}"? This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteUser}
              >
                Delete
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserManagement;
